/** Compact historical trade-outcome chart. Mock bars of realised RoR plus a
 * cumulative line. Green is not used to promote a high win rate.
 */
import { useMemo } from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import { FixedChart } from "@/cockpit/FixedChart";
import { AXIS, GRID, tooltipBox } from "@/cockpit/formatCockpit";

export function HistoricalOutcomesChart({ trades, highlightRegime, allowHighlight, height = 140, testid }) {
  const data = useMemo(
    () => (trades || []).map((t) => ({
      ...t,
      mark: t.ror >= 0 ? "▲" : "▼",
      rorPct: +(t.ror * 100).toFixed(2),
      cumPct: +(t.cumulative * 100).toFixed(2),
    })),
    [trades],
  );
  if (!data.length) {
    return <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>No historical observations in this fixture.</p>;
  }

  return (
    <div data-testid={testid}>
      <FixedChart height={height}>
        {(width) => (
          <ComposedChart width={width} height={height} data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="i" tick={{ fill: AXIS, fontSize: 8 }} />
            <YAxis tick={{ fill: AXIS, fontSize: 8 }} width={36} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              contentStyle={tooltipBox}
              formatter={(v, name) => [`${Number(v).toFixed(1)}%`, name === "rorPct" ? "RoR" : "Cumulative"]}
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload;
                return p ? `${p.date} · ${p.mark} · ${p.regime}` : "";
              }}
            />
            <ReferenceLine y={0} stroke="#3F3F46" />
            <Bar dataKey="rorPct" name="RoR" isAnimationActive={false} maxBarSize={8}>
              {data.map((d) => {
                const inReg = allowHighlight && d.currentRegime && d.regime === highlightRegime;
                return (
                  <Cell
                    key={d.i}
                    fill={d.ror >= 0 ? "#A1A1AA" : "#71717A"}
                    stroke={inReg ? "#E4E4E7" : "none"}
                    strokeWidth={inReg ? 1 : 0}
                  />
                );
              })}
            </Bar>
            <Line
              dataKey="cumPct"
              name="Cumulative"
              stroke="#E4E4E7"
              dot={false}
              strokeWidth={1.2}
              isAnimationActive={false}
            />
          </ComposedChart>
        )}
      </FixedChart>
      <div className="flex flex-wrap gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span>▲ / light bar · realised gain</span>
        <span>▼ / dark bar · realised loss</span>
        <span>— cumulative RoR</span>
        {allowHighlight ? <span>Outlined bars · current confirmed regime</span> : null}
      </div>
    </div>
  );
}
